import { GuideSection } from '@/components/guides/GuideSection';
import { GuideSteps, GuideStep } from '@/components/guides/GuideSteps';
import { GuideCallout } from '@/components/guides/GuideCallout';
import { GuideAction } from '@/components/guides/GuideAction';
import { GuideScreenshot } from '@/components/guides/GuideScreenshot';

export function ShareCompanyCodeSection() {
  return (
    <GuideSection
      id="share-company-code"
      eyebrow="Candidate onboarding"
      title="Share your company code"
      description="The company code links a candidate's NeuraX desktop app to your tenant on first launch."
    >
      <GuideScreenshot
        src="/guides/dashboard-overview.png"
        alt="Overview page showing the company code card above the KPI row, with reveal and copy buttons to the right of the masked code"
        caption="The company code card lives on the Overview page, right above your KPIs."
      />

      <GuideSteps>
        <GuideStep number={1} title="Open the Overview page">
          <p>
            Click <strong>Overview</strong> in the sidebar. You'll see a card titled
            <em> "Share this with your candidates"</em> with a masked code like
            <code>WOLFTC-••••-AE25</code>.
          </p>
          <GuideAction href="/">Open Overview</GuideAction>
        </GuideStep>

        <GuideStep number={2} title="Reveal and copy the code">
          <p>
            Click the <strong>eye icon</strong> to unmask it, then the <strong>copy icon</strong>
            to put it on your clipboard. You'll see a green check when it's copied.
          </p>
        </GuideStep>

        <GuideStep number={3} title="Send it to every candidate">
          <p>
            Put the code wherever you contact candidates — email, Slack, a welcome doc. They only
            need to enter it once, the first time they open the NeuraX desktop app.
          </p>
        </GuideStep>

        <GuideStep number={4} title="Watch the usage count">
          <p>
            The card shows <code>X / Y uses</code>. Each time a candidate registers their desktop
            app with your code, that counter ticks up. When you approach the cap, contact support
            and we'll raise it.
          </p>
        </GuideStep>
      </GuideSteps>

      <GuideCallout variant="info" title="What the code does">
        <p>
          It tells NeuraX "this install belongs to our team." Once registered, the candidate can
          receive licenses you issue, run interviews, and sync transcripts back to your portal — all
          scoped to your tenant. Candidates can't use NeuraX with your team without it.
        </p>
      </GuideCallout>

      <GuideCallout variant="warning" title="Don't post the code publicly">
        <p>
          The code caps total registrations; anyone who enters it can register a NeuraX install
          against your tenant. Keep it to people you actually want on the platform.
        </p>
      </GuideCallout>
    </GuideSection>
  );
}
